import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowLeft } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { AuthentipetLogo } from '../../../components/AuthentipetLogo';
import { AuthLayout } from './AuthLayout';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { authService } from '../../../services/authService';
import { APP_CONSTANTS } from '../../../constants';
import { logger } from '../../../lib/logger';

export const UpdatePassword: React.FC = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Check for token in URL query params
    useEffect(() => {
        const token = searchParams.get('token');
        if (!token) {
            setError('Invalid password reset link. Please request a new one.');
        }
    }, [searchParams]);

    const getPasswordStrength = (pass: string) => {
        let strength = 0;
        if (pass.length > 5) strength += 1;
        if (pass.length > 7) strength += 1;
        if (/[0-9]/.test(pass)) strength += 1;
        if (/[^A-Za-z0-9]/.test(pass)) strength += 1;
        return strength;
    };

    const strength = getPasswordStrength(password);

    const getStrengthColor = (s: number) => {
        if (s === 0) return 'bg-gray-200';
        if (s <= 1) return 'bg-red-500';
        if (s === 2) return 'bg-orange-500';
        if (s === 3) return 'bg-yellow-500';
        return 'bg-green-500';
    };

    const getStrengthText = (s: number) => {
        if (s === 0) return '';
        if (s <= 1) return 'Weak';
        if (s === 2) return 'Fair';
        if (s === 3) return 'Good';
        return 'Strong';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const token = searchParams.get('token');
        if (!token) {
            setError('Invalid password reset link. Please request a new one.');
            return;
        }

        if (password !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        setIsLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            await authService.updateUserPassword(token, password);
            setSuccessMessage('Password updated successfully. Redirecting to login...');
            setTimeout(() => {
                navigate(APP_CONSTANTS.ROUTES.LOGIN);
            }, 2000);
        } catch (error: unknown) {
            logger.error('Update password error', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to update password. Please try again.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthLayout>
            <div className="sm:mx-auto sm:w-full sm:max-w-sm">
                <div className="flex justify-center">
                    <div className="bg-white/10 p-2 rounded-full">
                        <AuthentipetLogo size={80} />
                    </div>
                </div>
                <h2 className="mt-6 text-center text-3xl font-bold font-league-spartan uppercase tracking-[-2px] text-storgia-teal">
                    Set New Password
                </h2>
                <p className={`mt-2 text-center text-sm ${theme === 'dark' ? 'text-storgia-light-blurple/80' : 'text-gray-600'}`}>
                    Please enter your new password below
                </p>
            </div>

            <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
                <form className="space-y-6" onSubmit={handleSubmit}>
                    {error && (
                        <div className="p-3 rounded bg-red-500/10 border border-red-500/20 text-red-500 text-sm text-center animate-pulse">
                            {error}
                        </div>
                    )}

                    {successMessage && (
                        <div className="p-3 rounded bg-green-500/10 border border-green-500/20 text-green-500 text-sm text-center">
                            {successMessage}
                        </div>
                    )}

                    <div>
                        <Input
                            label="New Password"
                            id="password"
                            name="password"
                            type="password"
                            autoComplete="new-password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            leftIcon={<Lock className={`h-5 w-5 ${theme === 'dark' ? 'text-storgia-light-blurple/50' : 'text-gray-400'}`} />}
                            disabled={isLoading || !!successMessage}
                        />
                        {/* Password Strength Meter */}
                        {password && (
                            <div className="mt-2 space-y-1">
                                <div className="flex gap-1 h-1.5">
                                    {[1, 2, 3, 4].map((level) => (
                                        <div
                                            key={level}
                                            className={`flex-1 rounded-full transition-all duration-300 ${strength >= level ? getStrengthColor(strength) : 'bg-gray-200/20'
                                                }`}
                                        />
                                    ))}
                                </div>
                                <p className={`text-xs text-right font-medium transition-colors duration-300 ${strength === 0 ? 'text-gray-400' :
                                    strength <= 1 ? 'text-red-500' :
                                        strength === 2 ? 'text-orange-500' :
                                            strength === 3 ? 'text-yellow-500' :
                                                'text-green-500'
                                    }`}>
                                    {getStrengthText(strength)}
                                </p>
                            </div>
                        )}
                    </div>

                    <Input
                        label="Confirm Password"
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        leftIcon={<Lock className={`h-5 w-5 ${theme === 'dark' ? 'text-storgia-light-blurple/50' : 'text-gray-400'}`} />}
                        disabled={isLoading || !!successMessage}
                    />

                    <div>
                        <Button
                            type="submit"
                            disabled={isLoading || !!successMessage}
                            className="w-full bg-storgia-cta-pink hover:bg-storgia-cta-pink/90 text-white shadow-[0_0_20px_rgba(192,0,84,0.3)]"
                        >
                            {isLoading ? 'Updating...' : 'Update Password'}
                        </Button>
                    </div>
                </form>

                <div className="mt-6 text-center">
                    <Link to={APP_CONSTANTS.ROUTES.LOGIN} className={`flex items-center justify-center gap-2 text-sm font-medium transition-all ${theme === 'dark' ? 'text-storgia-light-blurple hover:text-white' : 'text-storgia-blurple hover:text-storgia-dark-blurple'
                        }`}>
                        <ArrowLeft className="h-4 w-4" />
                        Back to Login
                    </Link>
                </div>
            </div>
        </AuthLayout>
    );
};
